// src/pages/AdminUsersPage.js — Bloque 4 v2
import { useEffect, useState, useContext, useMemo } from 'react';
import api from '../api/api';
import { formatearFecha, fechaISO } from '../utils/fechas';
import { AuthContext } from '../context/AuthContext';

const font = "Montserrat, sans-serif";
const inputSx = { padding: "7px 10px", borderRadius: 8, border: "1.5px solid rgba(0,0,0,0.12)", fontSize: 12, fontFamily: font, color: "#163244", background: "#fff", outline: "none", boxSizing: "border-box" };
const labelSx = { display: "block", fontSize: 9, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 };

const ROLE_CFG = {
  admin:     { label: "Admin",     color: "#E7A432", bg: "rgba(231,164,50,0.12)" },
  user:      { label: "Cliente",   color: "#163244", bg: "rgba(22,50,68,0.08)"  },
  prestador: { label: "Prestador", color: "#1b7f4e", bg: "rgba(27,127,78,0.08)" },
};
const MONTHS_ES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function RoleBadge({ role }) {
  const cfg = ROLE_CFG[role] || { label: role, color: "#7a93a4", bg: "#f0f5f8" };
  return <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 700, color: cfg.color, background: cfg.bg, whiteSpace: "nowrap" }}>{cfg.label.toUpperCase()}</span>;
}

function StatusBadge({ user }) {
  if (user.deleted_at) {
    return <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 700, color: "#7a93a4", background: "rgba(0,0,0,0.05)", whiteSpace: "nowrap" }}>Dada de baja</span>;
  }
  const isLocked = user.locked_until && new Date() < new Date(user.locked_until);
  if (isLocked) {
    const mins = Math.ceil((new Date(user.locked_until) - new Date()) / 60000);
    return <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 700, color: "#c0392b", background: "rgba(192,57,43,0.08)", whiteSpace: "nowrap" }}>🔒 {mins}m</span>;
  }
  return <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 700, color: "#1b7f4e", background: "rgba(27,127,78,0.08)", whiteSpace: "nowrap" }}>✓ Activo</span>;
}

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, []);
  return <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: type === "error" ? "#c0392b" : "#163244", color: "#fff", padding: "11px 22px", borderRadius: 10, fontWeight: 600, fontSize: 13, zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", borderLeft: `4px solid ${type === "error" ? "#e57373" : "#E7A432"}`, fontFamily: font }}>{msg}</div>;
}

// ─── Mini Donut ───────────────────────────────────────────────────────────────
function MiniDonut({ segments, size = 80, thick = 14 }) {
  const r = (size - thick) / 2;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((s, d) => s + d.value, 0) || 1;
  let off = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)", display: "block" }}>
      {segments.map((s, i) => {
        const pct = s.value / total;
        const dash = pct * circ;
        const el = <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={s.color} strokeWidth={thick} strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-off * circ} />;
        off += pct;
        return el;
      })}
    </svg>
  );
}

// ─── Bar chart horizontal ─────────────────────────────────────────────────────
function HBar({ label, value, max, color, total }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const pctOfTotal = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#163244", maxWidth: "65%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>{value} <span style={{ fontWeight: 400, color: "#aab8c2" }}>({pctOfTotal}%)</span></span>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: "#f0f5f8", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: color, transition: "width 0.6s" }} />
      </div>
    </div>
  );
}

// ─── Spark line ───────────────────────────────────────────────────────────────
function Spark({ vals, color = "#163244", h = 36 }) {
  if (!vals || vals.length < 2) return null;
  const mx = Math.max(...vals, 1);
  const W = 100;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * W},${h - (v / mx) * (h - 4) - 2}`).join(" ");
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <polygon points={`0,${h} ${pts} ${W},${h}`} fill={color + "18"} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Drawer detalle usuario ───────────────────────────────────────────────────
function UserDrawer({ user, currentUserId, onClose, onUpdate, onDelete, onUnlock, onReset }) {
  const [tab,   setTab]   = useState("info");
  const [form,  setForm]  = useState({ name: user.name||"", email: user.email||"", role: user.role||"user", phone: user.phone||"", address: user.address||"", dni: user.dni||"", birthdate: fechaISO(user.birthdate), city: user.city||"", province: user.province||"", street: user.street||"", postal_code: user.postal_code||"" });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");
  const isLocked = user.locked_until && new Date() < new Date(user.locked_until);
  const isSelf   = user.id === currentUserId;

  const handleSave = async () => {
    if (!form.name || !form.email || !form.role) { setError("Nombre, email y rol son obligatorios."); return; }
    setSaving(true); setError("");
    try { const res = await api.put(`/users/admin-op/${user.id}`, form); onUpdate(res.data); }
    catch (err) { setError(err.response?.data?.error || "Error al guardar."); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 18, width: "min(500px,100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 16px 60px rgba(22,50,68,0.22)" }}>
        {/* Header */}
        <div style={{ background: "#163244", padding: "18px 22px", borderRadius: "18px 18px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 90% 20%, rgba(231,164,50,0.15) 0%, transparent 55%)", pointerEvents: "none" }} />
          <div style={{ zIndex: 1, display: "flex", gap: 12, alignItems: "center" }}>
            {user.photo_url
              ? <img src={user.photo_url} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #E7A432" }} />
              : <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#E7A432", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: "#163244" }}>{(user.first_name || user.name)?.[0]?.toUpperCase() || "?"}</div>
            }
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{user.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : user.name}</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>{user.email}</div>
              <div style={{ display: "flex", gap: 5, marginTop: 5 }}><RoleBadge role={user.role} /><StatusBadge user={user} /></div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 28, height: 28, borderRadius: "50%", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>✕</button>
        </div>
        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid rgba(0,0,0,0.07)" }}>
          {[["info","Información"],["edit","Editar"]].map(([id,lbl]) => (
            <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: "11px", border: "none", background: tab===id?"#fff":"#f8fafb", borderBottom: tab===id?"2px solid #163244":"2px solid transparent", color: tab===id?"#163244":"#7a93a4", fontFamily: font, fontWeight: 700, fontSize: 11, cursor: "pointer" }}>{lbl.toUpperCase()}</button>
          ))}
        </div>
        <div style={{ padding: "18px 22px" }}>
          {tab === "info" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, background: "#f5f7fa", borderRadius: 10, padding: "14px", marginBottom: 12 }}>
                {[
                  ["Nombre completo", user.first_name ? `${user.first_name} ${user.last_name||""}`.trim() : user.name],
                  ["Username", user.username || "—"],
                  ["Email", user.email],
                  ["Teléfono", user.phone || "—"],
                  ["DNI", user.dni || "—"],
                  ["Nacimiento", user.birthdate ? formatearFecha(user.birthdate) : "—"],
                  ["Provincia", user.province || "—"],
                  ["Ciudad", user.city || "—"],
                  ["Calle", user.street || user.address || "—"],
                  ["Cod. Postal", user.postal_code || "—"],
                  ["Registro", new Date(user.created_at).toLocaleDateString("es-AR")],
                  ["Intentos fallidos", user.failed_attempts || 0],
                ].map(([lbl, val]) => (
                  <div key={lbl}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#aab8c2", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lbl}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#163244", wordBreak: "break-all" }}>{val}</div>
                  </div>
                ))}
              </div>
              {/* Verificaciones */}
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {[["✉️ Email", user.email_verified], ["📱 Teléfono", user.phone_verified], ["🪪 Identidad", user.is_verified]].map(([lbl, ok]) => (
                  <div key={lbl} style={{ flex: 1, padding: "8px 10px", borderRadius: 8, background: ok ? "rgba(27,127,78,0.07)" : "rgba(192,57,43,0.07)", border: `1px solid ${ok ? "rgba(27,127,78,0.2)" : "rgba(192,57,43,0.2)"}`, textAlign: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: ok ? "#1b7f4e" : "#c0392b" }}>{lbl}</div>
                    <div style={{ fontSize: 10, color: ok ? "#1b7f4e" : "#c0392b", fontWeight: 700 }}>{ok ? "✓" : "✕"}</div>
                  </div>
                ))}
              </div>
              {isLocked && <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 9, padding: "10px 12px", fontSize: 12, color: "#c0392b", fontWeight: 600, marginBottom: 12 }}>🔒 Bloqueado hasta {new Date(user.locked_until).toLocaleString("es-AR")}</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {isLocked && <button onClick={onUnlock} style={{ padding: "10px", borderRadius: 50, border: "none", background: "#1b7f4e", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>🔓 Desbloquear cuenta</button>}
                <button onClick={onReset} style={{ padding: "10px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>🔑 Restablecer contraseña</button>
                {!isSelf && !user.deleted_at && <button onClick={onDelete} style={{ padding: "10px", borderRadius: 50, border: "1.5px solid rgba(192,57,43,0.3)", background: "transparent", color: "#c0392b", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>🗑 Eliminar usuario</button>}
              </div>
            </div>
          )}
          {tab === "edit" && (
            <div>
              {error && <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#c0392b", marginBottom: 12 }}>⚠ {error}</div>}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                {[["name","Nombre *"],["email","Email *"],["phone","Teléfono"],["dni","DNI"],["city","Ciudad"],["province","Provincia"],["street","Calle"],["postal_code","Cod. Postal"]].map(([k,lbl]) => (
                  <div key={k}><label style={labelSx}>{lbl}</label><input style={{ ...inputSx, width: "100%" }} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} /></div>
                ))}
                <div><label style={labelSx}>Fecha nacimiento</label><input type="date" style={{ ...inputSx, width: "100%" }} value={form.birthdate} onChange={e => setForm(f => ({ ...f, birthdate: e.target.value }))} /></div>
                <div><label style={labelSx}>Rol *</label>
                  <select style={{ ...inputSx, width: "100%", cursor: "pointer" }} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="user">Cliente</option><option value="prestador">Prestador</option><option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={onClose} style={{ flex: 1, padding: "10px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Cancelar</button>
                <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: "10px", borderRadius: 50, border: "none", background: "#E7A432", color: "#163244", fontFamily: font, fontWeight: 800, fontSize: 12, cursor: saving?"not-allowed":"pointer" }}>{saving?"Guardando…":"Guardar cambios"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const AdminUsersPage = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users,         setUsers]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [selected,      setSelected]      = useState(null);
  const [toast,         setToast]         = useState(null);
  const [search,        setSearch]        = useState('');
  const [roleFilter,    setRoleFilter]    = useState('');
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [page,          setPage]          = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resultadoBaja, setResultadoBaja] = useState(null);
  const PAGE_SIZE = 25;

  const showToast = (msg, type = "success") => setToast({ msg, type });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true); setError(null);
    try { const res = await api.get('/users'); setUsers(res.data); }
    catch (err) { setError(err.response?.data?.error || 'Error al cargar usuarios.'); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) ||
      u.phone?.includes(q) || u.dni?.includes(q) ||
      u.city?.toLowerCase().includes(q) || u.province?.toLowerCase().includes(q) ||
      u.first_name?.toLowerCase().includes(q) || u.last_name?.toLowerCase().includes(q);
    return matchSearch && (!roleFilter || u.role === roleFilter);
  }), [users, search, roleFilter]);

  const paginated  = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  // ── Estadísticas para sidebar ──────────────────────────────────────────────
  const stats = useMemo(() => {
    const byRole = { user: 0, prestador: 0, admin: 0 };
    const byProvince = {};
    const byMonth = {};
    let bloqueados = 0, emailVerif = 0, phoneVerif = 0, nuevos7d = 0, nuevos30d = 0;
    const now = new Date();
    users.forEach(u => {
      byRole[u.role] = (byRole[u.role] || 0) + 1;
      if (u.locked_until && new Date() < new Date(u.locked_until)) bloqueados++;
      if (u.email_verified) emailVerif++;
      if (u.phone_verified) phoneVerif++;
      const d = new Date(u.created_at);
      if (now - d < 7*86400000)  nuevos7d++;
      if (now - d < 30*86400000) nuevos30d++;
      if (u.province) byProvince[u.province] = (byProvince[u.province] || 0) + 1;
      const mk = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      byMonth[mk] = (byMonth[mk] || 0) + 1;
    });
    // Últimos 6 meses
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i);
      const mk = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      months.push({ label: MONTHS_ES[d.getMonth()], value: byMonth[mk] || 0 });
    }
    const provinces = Object.entries(byProvince).sort((a,b) => b[1]-a[1]).slice(0,8).map(([p,v]) => ({ label: p, value: v }));
    return { byRole, bloqueados, emailVerif, phoneVerif, nuevos7d, nuevos30d, months, provinces, total: users.length };
  }, [users]);

  const handleUpdate = (updated) => {
    setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
    setSelected(prev => ({ ...prev, ...updated }));
    showToast("Usuario actualizado.");
  };

  // La baja (DELETE /users/admin-op/:id, services/bajaDeCuenta.js del backend)
  // puede no ejecutarse: 409 con los bloqueos (algo en curso), 409 si ya
  // estaba dada de baja, 404. Hasta el 28-10-2026 esto se tragaba el error y
  // sacaba la fila igual: el panel decía "eliminado" cuando no lo estaba.
  // Ahora se muestra el resultado de cada uno y la lista se vuelve a pedir,
  // así lo que se ve es lo que hay en la base. Desde el panel no se fuerza.
  const handleDelete = async (ids) => {
    setConfirmDelete(null);
    const resultados = [];
    for (const id of ids) {
      const nombre = users.find(u => u.id === id)?.name || `#${id}`;
      try {
        await api.delete(`/users/admin-op/${id}`);
        resultados.push({ id, nombre, ok: true });
      } catch (err) {
        const data = err.response?.data || {};
        resultados.push({
          id, nombre, ok: false,
          error: data.error || "No se pudo eliminar (error de red o del servidor).",
          bloqueos: data.bloqueos || [],
          forzable: data.forzable,
          siSeFuerza: data.si_se_fuerza || [],
        });
      }
    }
    setSelected(null); setSelectedIds(new Set());
    await fetchUsers();
    const bien = resultados.filter(r => r.ok).length;
    const mal = resultados.length - bien;
    if (mal) setResultadoBaja(resultados);
    showToast(
      mal ? `${bien} eliminado${bien === 1 ? "" : "s"}, ${mal} sin eliminar. Mirá el detalle.`
          : `${bien} usuario${bien > 1 ? "s" : ""} eliminado${bien > 1 ? "s" : ""}.`,
      mal ? "error" : "success",
    );
  };

  const handleUnlock = async (u) => {
    try {
      await api.post(`/users/admin-op/unlock/${u.id}`);
      setUsers(prev => prev.map(x => x.id===u.id ? {...x, failed_attempts:0, locked_until:null} : x));
      setSelected(prev => ({...prev, failed_attempts:0, locked_until:null}));
      showToast(`Cuenta de ${u.name} desbloqueada.`);
    } catch (err) { showToast(err.response?.data?.error||"Error.", "error"); }
  };

  const handleReset = async (u) => {
    try { const res = await api.post(`/users/admin-op/reset-password/${u.id}`); showToast(res.data.message); }
    catch (err) { showToast(err.response?.data?.error||"Error.", "error"); }
  };

  const toggleId  = id => setSelectedIds(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleAll = () => setSelectedIds(selectedIds.size===paginated.length ? new Set() : new Set(paginated.map(u=>u.id)));

  const maxProvince = Math.max(...stats.provinces.map(p=>p.value), 1);
  const maxMonth    = Math.max(...stats.months.map(m=>m.value), 1);

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "22px 20px 50px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 260px", gap: 16, alignItems: "start" }}>

          {/* ── COLUMNA PRINCIPAL ──────────────────────────────────────── */}
          <div>
            {/* Header */}
            <div style={{ marginBottom: 14 }}>
              <h1 style={{ margin: "0 0 3px", fontSize: 19, fontWeight: 800, color: "#163244" }}>Usuarios</h1>
              <p style={{ margin: 0, color: "#7a93a4", fontSize: 12 }}>{users.length} usuarios registrados · {stats.nuevos30d} nuevos este mes</p>
            </div>

            {/* KPIs compactos */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 12 }}>
              {[
                { label:"Total",       val: stats.total,      col:"#163244", icon:"👥" },
                { label:"Clientes",    val: stats.byRole.user||0, col:"#163244", icon:"🧑" },
                { label:"Prestadores", val: stats.byRole.prestador||0, col:"#1b7f4e", icon:"🔧" },
                { label:"Bloqueados",  val: stats.bloqueados, col:"#c0392b", icon:"🔒" },
                { label:"Nuevos 7d",   val: stats.nuevos7d,   col:"#7b5ea7", icon:"✨" },
              ].map(s => (
                <div key={s.label} style={{ background:"#fff", borderRadius:10, border:"1px solid rgba(0,0,0,0.07)", padding:"10px 12px" }}>
                  <div style={{ fontSize:9, fontWeight:700, color:"#7a93a4", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>{s.icon} {s.label}</div>
                  <div style={{ fontSize:20, fontWeight:900, color:s.col, lineHeight:1 }}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* Filtros */}
            <div style={{ display:"flex", gap:8, marginBottom:10, alignItems:"center", flexWrap:"wrap" }}>
              <input style={{ ...inputSx, flex:"1 1 200px", minWidth:0 }} placeholder="🔍  Nombre, email, DNI, ciudad, provincia..." value={search} onChange={e=>{ setSearch(e.target.value); setPage(0); }} />
              <div style={{ display:"flex", gap:3 }}>
                {[["","Todos"],["user","Clientes"],["prestador","Prestadores"],["admin","Admins"]].map(([v,l]) => (
                  <button key={v} onClick={()=>{ setRoleFilter(v); setPage(0); }} style={{ padding:"5px 11px", borderRadius:20, border:`1.5px solid ${roleFilter===v?"#163244":"rgba(0,0,0,0.1)"}`, background:roleFilter===v?"#163244":"#fff", color:roleFilter===v?"#fff":"#5a6e7f", fontFamily:font, fontWeight:600, fontSize:11, cursor:"pointer" }}>{l}</button>
                ))}
              </div>
              <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
                {selectedIds.size > 0 && (
                  <button onClick={()=>setConfirmDelete({ids:[...selectedIds]})} style={{ padding:"6px 12px", borderRadius:50, background:"rgba(192,57,43,0.08)", border:"1.5px solid rgba(192,57,43,0.2)", color:"#c0392b", fontFamily:font, fontWeight:700, fontSize:11, cursor:"pointer" }}>🗑 ({selectedIds.size})</button>
                )}
                <button onClick={fetchUsers} style={{ padding:"6px 14px", borderRadius:50, background:"#163244", color:"#fff", border:"none", fontFamily:font, fontWeight:700, fontSize:11, cursor:"pointer" }}>↻ Actualizar</button>
              </div>
            </div>

            {error && <div style={{ background:"rgba(192,57,43,0.07)", border:"1px solid rgba(192,57,43,0.2)", borderRadius:8, padding:"9px 12px", fontSize:12, color:"#c0392b", marginBottom:10 }}>⚠ {error}</div>}

            {/* Tabla */}
            <div style={{ background:"#fff", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", overflow:"hidden" }}>
              {/* Header */}
              <div style={{ display:"grid", gridTemplateColumns:"32px 1.8fr 1.6fr 0.7fr 0.7fr 0.9fr 0.8fr 60px", padding:"9px 14px", background:"#163244", gap:6, alignItems:"center" }}>
                <input type="checkbox" checked={selectedIds.size===paginated.length&&paginated.length>0} onChange={toggleAll} style={{ cursor:"pointer", accentColor:"#E7A432" }} />
                {["Usuario","Email","Rol","Estado","Registro","Ubicación",""].map((h,i) => (
                  <div key={i} style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.55)", textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</div>
                ))}
              </div>

              {loading && <div style={{ textAlign:"center", padding:"32px", color:"#7a93a4", fontSize:12 }}><span style={{ display:"inline-block", width:20, height:20, border:"2px solid #dce5ec", borderTopColor:"#163244", borderRadius:"50%", animation:"spin 0.7s linear infinite", marginBottom:8 }} /><br/>Cargando…</div>}

              {!loading && paginated.map((u, i) => {
                const isLocked = u.locked_until && new Date() < new Date(u.locked_until);
                const displayName = u.first_name ? `${u.first_name} ${u.last_name||""}`.trim() : u.name;
                const location = [u.city, u.province].filter(Boolean).join(", ") || "—";
                return (
                  <div key={u.id}
                    style={{ display:"grid", gridTemplateColumns:"32px 1.8fr 1.6fr 0.7fr 0.7fr 0.9fr 0.8fr 60px", padding:"10px 14px", gap:6, alignItems:"center", borderBottom:i<paginated.length-1?"1px solid rgba(0,0,0,0.04)":"none", background:selectedIds.has(u.id)?"rgba(22,50,68,0.03)":"transparent", transition:"background 0.12s" }}
                    onMouseEnter={e=>{ if(!selectedIds.has(u.id)) e.currentTarget.style.background="#fafcfe"; }}
                    onMouseLeave={e=>{ if(!selectedIds.has(u.id)) e.currentTarget.style.background="transparent"; }}
                  >
                    <input type="checkbox" checked={selectedIds.has(u.id)} onChange={()=>toggleId(u.id)} style={{ cursor:"pointer", accentColor:"#163244" }} />
                    {/* Usuario */}
                    <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
                      {u.photo_url
                        ? <img src={u.photo_url} alt="" style={{ width:28, height:28, borderRadius:"50%", objectFit:"cover", flexShrink:0 }} />
                        : <div style={{ width:28, height:28, borderRadius:"50%", background:isLocked?"#fdecea":"#163244", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:isLocked?"#c0392b":"#E7A432", flexShrink:0 }}>{displayName?.[0]?.toUpperCase()||"?"}</div>
                      }
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:11, color:"#163244", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{displayName}</div>
                        {u.phone && <div style={{ fontSize:10, color:"#aab8c2" }}>{u.phone}</div>}
                      </div>
                    </div>
                    {/* Email */}
                    <div style={{ fontSize:11, color:"#5a6e7f", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.email}</div>
                    {/* Rol */}
                    <div><RoleBadge role={u.role} /></div>
                    {/* Estado */}
                    <div><StatusBadge user={u} /></div>
                    {/* Registro */}
                    <div style={{ fontSize:10, color:"#7a93a4" }}>{new Date(u.created_at).toLocaleDateString("es-AR")}</div>
                    {/* Ubicación */}
                    <div style={{ fontSize:10, color:"#7a93a4", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={location}>📍 {location}</div>
                    {/* Acción */}
                    <div><button onClick={()=>setSelected(u)} style={{ padding:"4px 10px", borderRadius:50, background:"#163244", border:"none", color:"#fff", fontFamily:font, fontWeight:600, fontSize:10, cursor:"pointer" }}>Ver →</button></div>
                  </div>
                );
              })}

              {!loading && paginated.length === 0 && <div style={{ textAlign:"center", padding:"32px", color:"#aab8c2", fontSize:12 }}>No se encontraron usuarios</div>}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10 }}>
                <div style={{ fontSize:11, color:"#7a93a4" }}>{filtered.length} usuario{filtered.length!==1?"s":""} · pág. {page+1} de {totalPages} · mostrando {PAGE_SIZE} por página</div>
                <div style={{ display:"flex", gap:3 }}>
                  <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(0,0,0,0.1)", background:"#fff", color:page===0?"#ccc":"#163244", fontFamily:font, fontWeight:600, fontSize:11, cursor:page===0?"not-allowed":"pointer" }}>← Ant.</button>
                  {Array.from({length:Math.min(totalPages,7)},(_,i)=>{
                    const pg=totalPages<=7?i:Math.max(0,Math.min(page-3,totalPages-7))+i;
                    return <button key={pg} onClick={()=>setPage(pg)} style={{ width:28, height:28, borderRadius:7, border:`1px solid ${pg===page?"#163244":"rgba(0,0,0,0.1)"}`, background:pg===page?"#163244":"#fff", color:pg===page?"#fff":"#163244", fontFamily:font, fontWeight:700, fontSize:11, cursor:"pointer" }}>{pg+1}</button>;
                  })}
                  <button onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page===totalPages-1} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(0,0,0,0.1)", background:"#fff", color:page===totalPages-1?"#ccc":"#163244", fontFamily:font, fontWeight:600, fontSize:11, cursor:page===totalPages-1?"not-allowed":"pointer" }}>Sig. →</button>
                </div>
              </div>
            )}
          </div>

          {/* ── SIDEBAR ESTADÍSTICAS ───────────────────────────────────── */}
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

            {/* Distribución por rol — donut */}
            <div style={{ background:"#fff", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", overflow:"hidden" }}>
              <div style={{ padding:"12px 14px 0", display:"flex", alignItems:"center", gap:7 }}>
                <div style={{ width:3, height:12, background:"#E7A432", borderRadius:2 }} />
                <span style={{ fontSize:11, fontWeight:700, color:"#163244" }}>Distribución por rol</span>
              </div>
              <div style={{ padding:"12px 14px 14px", display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ position:"relative", flexShrink:0 }}>
                  <MiniDonut segments={[
                    { value: stats.byRole.user||0,      color:"#163244" },
                    { value: stats.byRole.prestador||0, color:"#1b7f4e" },
                    { value: stats.byRole.admin||0,     color:"#E7A432" },
                  ]} size={80} thick={14} />
                  <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                    <div style={{ fontSize:16, fontWeight:900, color:"#163244", lineHeight:1 }}>{stats.total}</div>
                    <div style={{ fontSize:8, color:"#aab8c2", fontWeight:600 }}>TOTAL</div>
                  </div>
                </div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                  {[["Clientes",stats.byRole.user||0,"#163244"],["Prestadores",stats.byRole.prestador||0,"#1b7f4e"],["Admins",stats.byRole.admin||0,"#E7A432"]].map(([l,v,c]) => (
                    <div key={l} style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                        <div style={{ width:7, height:7, borderRadius:"50%", background:c }} />
                        <span style={{ fontSize:11, color:"#163244" }}>{l}</span>
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, color:c }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Verificaciones */}
            <div style={{ background:"#fff", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10 }}>
                <div style={{ width:3, height:12, background:"#E7A432", borderRadius:2 }} />
                <span style={{ fontSize:11, fontWeight:700, color:"#163244" }}>Verificaciones</span>
              </div>
              {[
                ["✉️ Email",    stats.emailVerif, "#1b7f4e"],
                ["📱 Teléfono", stats.phoneVerif, "#163244"],
              ].map(([l,v,c]) => (
                <HBar key={l} label={l} value={v} max={stats.total} color={c} total={stats.total} />
              ))}
            </div>

            {/* Crecimiento mensual — spark + bars */}
            <div style={{ background:"#fff", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:8 }}>
                <div style={{ width:3, height:12, background:"#E7A432", borderRadius:2 }} />
                <span style={{ fontSize:11, fontWeight:700, color:"#163244" }}>Crecimiento (6 meses)</span>
              </div>
              <div style={{ marginBottom:8 }}>
                <Spark vals={stats.months.map(m=>m.value)} color="#163244" h={40} />
              </div>
              <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:48 }}>
                {stats.months.map((m,i) => {
                  const h = maxMonth > 0 ? Math.max((m.value/maxMonth)*40, m.value>0?3:0) : 0;
                  return (
                    <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2, height:"100%", justifyContent:"flex-end" }}>
                      <div style={{ width:"100%", borderRadius:"2px 2px 0 0", background:"#163244", height:`${h}px` }} />
                      <div style={{ fontSize:8, color:"#aab8c2", fontWeight:600 }}>{m.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Por provincia */}
            {stats.provinces.length > 0 && (
              <div style={{ background:"#fff", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", padding:"12px 14px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10 }}>
                  <div style={{ width:3, height:12, background:"#E7A432", borderRadius:2 }} />
                  <span style={{ fontSize:11, fontWeight:700, color:"#163244" }}>Por provincia</span>
                </div>
                {stats.provinces.map(p => (
                  <HBar key={p.label} label={p.label} value={p.value} max={maxProvince} color="#7b5ea7" total={stats.total} />
                ))}
              </div>
            )}

            {/* Estado de cuentas */}
            <div style={{ background:"#fff", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10 }}>
                <div style={{ width:3, height:12, background:"#E7A432", borderRadius:2 }} />
                <span style={{ fontSize:11, fontWeight:700, color:"#163244" }}>Estado de cuentas</span>
              </div>
              {[
                ["Activas",    stats.total-stats.bloqueados, "#1b7f4e"],
                ["Bloqueadas", stats.bloqueados, "#c0392b"],
                ["Nuevas 7d",  stats.nuevos7d,  "#7b5ea7"],
                ["Nuevas 30d", stats.nuevos30d, "#163244"],
              ].map(([l,v,c]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid rgba(0,0,0,0.04)", fontSize:11 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <div style={{ width:6, height:6, borderRadius:"50%", background:c }} />
                    <span style={{ color:"#163244" }}>{l}</span>
                  </div>
                  <span style={{ fontWeight:700, color:c }}>{v}</span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>

      {/* Drawer */}
      {selected && <UserDrawer user={selected} currentUserId={currentUser?.id} onClose={()=>setSelected(null)} onUpdate={handleUpdate} onDelete={()=>setConfirmDelete({ids:[selected.id]})} onUnlock={()=>handleUnlock(selected)} onReset={()=>handleReset(selected)} />}

      {/* Confirm delete */}
      {confirmDelete && (
        <div style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(22,50,68,0.55)", backdropFilter:"blur(3px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#fff", borderRadius:18, padding:"24px", width:"min(380px,90vw)", boxShadow:"0 16px 60px rgba(22,50,68,0.2)", textAlign:"center" }}>
            <div style={{ fontSize:32, marginBottom:10 }}>⚠️</div>
            <div style={{ fontSize:15, fontWeight:800, color:"#163244", marginBottom:6 }}>¿Confirmar eliminación?</div>
            <div style={{ fontSize:12, color:"#7a93a4", marginBottom:20 }}>Vas a eliminar {confirmDelete.ids.length} usuario{confirmDelete.ids.length>1?"s":""}. Esta acción no se puede deshacer. Si alguno tiene algo en curso, no se elimina y te mostramos qué falta.</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>setConfirmDelete(null)} style={{ flex:1, padding:"10px", borderRadius:50, border:"1.5px solid rgba(0,0,0,0.12)", background:"transparent", color:"#7a93a4", fontFamily:font, fontWeight:600, fontSize:12, cursor:"pointer" }}>Cancelar</button>
              <button onClick={()=>handleDelete(confirmDelete.ids)} style={{ flex:1, padding:"10px", borderRadius:50, border:"none", background:"#c0392b", color:"#fff", fontFamily:font, fontWeight:700, fontSize:12, cursor:"pointer" }}>Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* Resultado de la baja: solo si alguno no se eliminó. Los textos de
          los bloqueos son los mismos que ve el usuario en la app. */}
      {resultadoBaja && (
        <div style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(22,50,68,0.55)", backdropFilter:"blur(3px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#fff", borderRadius:18, padding:"24px", width:"min(560px,92vw)", maxHeight:"85vh", overflowY:"auto", boxShadow:"0 16px 60px rgba(22,50,68,0.2)" }}>
            <div style={{ fontSize:15, fontWeight:800, color:"#163244", marginBottom:14 }}>Resultado de la baja</div>
            {resultadoBaja.map(r => (
              <div key={r.id} style={{ borderTop:"1px solid rgba(0,0,0,0.06)", padding:"12px 0" }}>
                <div style={{ fontSize:13, fontWeight:700, color: r.ok ? "#1b7f4e" : "#c0392b" }}>
                  {r.ok ? "✓" : "✗"} {r.nombre} <span style={{ color:"#aab8c2", fontWeight:600 }}>(#{r.id})</span>
                  {r.ok ? " — eliminado" : " — NO se eliminó"}
                </div>
                {!r.ok && (
                  <div style={{ fontSize:12, color:"#5a6e7f", marginTop:6 }}>
                    <div style={{ fontWeight:600 }}>{r.error}</div>
                    {r.bloqueos.length > 0 && (
                      <ul style={{ margin:"6px 0 0", paddingLeft:18 }}>
                        {r.bloqueos.map((b, i) => <li key={i} style={{ marginBottom:4 }}>{b.texto}</li>)}
                      </ul>
                    )}
                    {r.siSeFuerza.length > 0 && (
                      <div style={{ marginTop:8, background:"rgba(231,164,50,0.1)", borderRadius:9, padding:"8px 10px" }}>
                        <div style={{ fontWeight:700, color:"#163244", marginBottom:4 }}>
                          {r.forzable ? "Si se fuerza (con motivo, fuera del panel):" : "No se puede forzar."}
                        </div>
                        {r.forzable && (
                          <ul style={{ margin:0, paddingLeft:18 }}>
                            {r.siSeFuerza.map((a, i) => <li key={i} style={{ marginBottom:4 }}>{a}</li>)}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <button onClick={()=>setResultadoBaja(null)} style={{ marginTop:14, width:"100%", padding:"10px", borderRadius:50, border:"none", background:"#163244", color:"#fff", fontFamily:font, fontWeight:700, fontSize:12, cursor:"pointer" }}>Cerrar</button>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)} />}

      <style>{`
        * { box-sizing: border-box; }
        input:focus, select:focus { border-color: #163244 !important; outline: none; box-shadow: 0 0 0 3px rgba(22,50,68,0.08); }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default AdminUsersPage;
